import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import CartBar from '../../components/MerchantDetails/CartBar';
import MobileCartBar from '../../components/MerchantDetails/MobileCartBar';
import Footer from '../../components/Footer';
import { AiOutlineSearch } from 'react-icons/ai';
import SearchedProducts from './SearchedProducts';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import { useUser } from '../../contexts/user';
import log from '../../utils/logger';
import api from '../../api/api';

const StoreSearch = () => {
  const { t, i18n } = useTranslation();
  const { merchantConfigId } = useParams();
  const { user, idToken, adminImpersonate } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParam = new URLSearchParams(location.search).get('query') || '';
  const [searchTerm, setSearchTerm] = useState(queryParam);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(queryParam);
  const [searchResults, setSearchResults] = useState([]);

  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

    const fetchResults = async () => {
      const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

      if (!merchantConfigId || !isValidObjectId(merchantConfigId)) {
        log.error('Invalid or missing merchantConfigId:', merchantConfigId);
        return;
      }

      try {
        const response = await api.get(`/buyer/search/store/${merchantConfigId}`, {
          params: {
            search: debouncedSearchTerm,
            language: i18n.language,
            adminImpersonateToken: adminImpersonate || null,
          },
          headers: user && idToken ? { Authorization: `Bearer ${idToken}` } : {},
        });

        setSearchResults(response.data.response.data || []);
      } catch (error) {
        log.error('Error fetching search results:', error);
      }
    };

    fetchResults();
  }, [debouncedSearchTerm, merchantConfigId, i18n.language]);

  return (
    <>
      <div className="flex flex-col min-h-dvh">
        <div className='mb-20 lg:mb-0'>
          <Header />

          <section className='pb-32'>
            {/* Search bar */}
            <div className='px-3 md:px-7 py-6 my-4 sticky top-[46px] sm:top-[57px] lg:top-16 z-20 bg-white border-b border-[#0000001a]'>
              <div className='rounded-lg flex items-center gap-2 px-3 bg-[#eee] h-12'>

                {/* Mobile back button */}
                <button
                  onClick={() => navigate(`/merchant-details/${merchantConfigId}`)}
                  className="lg:hidden flex items-center justify-center w-8 h-8"
                >
                  <FaChevronLeft className="text-lg opacity-70" />
                </button>

                {/* Search input */}
                <input
                  ref={inputRef}
                  type="search"
                  className='flex-1 h-full outline-none border-none bg-transparent'
                  placeholder={t('search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {/* Search icon */}
                <AiOutlineSearch className='text-2xl opacity-60 hidden lg:block' />
                <AiOutlineSearch className='text-2xl opacity-60 lg:hidden' />

              </div>
            </div>

            {/* Recently searched keywords */}
            {/* <div className='mt-6 px-3 md:px-7 mb-10'>
              <div className='flex items-center justify-between'>
                <h2 className='text-lg font-medium'>{t('Recent Search')}</h2>
                <button
                  onClick={clearSearches}
                  className='px-4 text-[#1E88E5] text-sm font-semibold h-9 flex items-center justify-center transition-all duration-500 capitalize hover:bg-[#DAEAFB] rounded'>
                  {t('Clear')}
                </button>
              </div>
            </div> */}

            {/* Render search results */}
            {searchTerm.trim() === '' ? (
              <div className="px-3 md:px-7 py-6 text-center text-[#b5b5b5]">
                {t('Type something to start searching')}
              </div>
            ) : searchResults.length > 0 ? (
              <SearchedProducts products={searchResults} />
            ) : (
              <div className="px-3 md:px-7 py-6 text-center text-[#b5b5b5]">
                {t('No products found')}
              </div>
            )}
          </section>

          <CartBar />
          <MobileCartBar />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default StoreSearch;
